import type { OnlineEventViewDto, OnlineEventSpeakerViewDto, QuizDto, WebViewDto } from '@/api/webs'
import { webTypeKey, type WebTypeKey } from '@/constants/webTypes'
import { formatBytes } from '@/utils/fileSize'
import { isOnDemandMode, isOrbitalXRoom } from '@/utils/onlineEventMode'
import { calendarDate, calendarDaysUntil, eventClock, eventSpan, type EventClock } from './dates'

/**
 * What a web's home reads off the web, by type (ORB-946).
 *
 * ⚑ One layout for every type: the number the web is judged on and its sign-off on top, then the
 * Overview beside one column that changes with the type. Everything here is a projection of
 * `WebViewDto` — the page adds no vocabulary the web does not already carry.
 */

/** Which of the four pages this web gets. `other` is a type this dashboard does not know yet. */
export type WebHomeKind = WebTypeKey | 'other'

export function webHomeKind(web: Pick<WebViewDto, 'Type'>): WebHomeKind {
  return webTypeKey(web.Type) ?? 'other'
}

/** The per-type block that holds the page's own words: the public title and the overview. */
function typeBlock(web: WebViewDto): { Title?: string; Overview?: string } | undefined {
  switch (webHomeKind(web)) {
    case 'OnlineEvent': return web.OnlineEvent
    case 'Download': return web.Download
    case 'Meeting': return web.Meeting
    case 'Demo': return web.Demo
    default: return undefined
  }
}

/** The three states a web's sign-off can be in, as the page draws them. */
export type SignOffState = 'blocked' | 'ready' | 'done'

/**
 * ⚑ Signed off wins over anything outstanding. It is the web's state of record, and a signed-off web
 * whose fields have since changed is still the one that is live; the readiness list is about the next
 * sign-off, and matters again once the web is reopened.
 */
export function signOffState(web: Pick<WebViewDto, 'ApprovalStatus' | 'OutstandingForSignOff'>): SignOffState {
  if (web.ApprovalStatus === 'Signed Off') return 'done'
  return (web.OutstandingForSignOff?.length ?? 0) > 0 ? 'blocked' : 'ready'
}

/** The title the landing page and the emails use, or null when nobody has written one yet. */
export function publicTitle(web: WebViewDto): string | null {
  return typeBlock(web)?.Title?.trim() || null
}

/** The overview text, as the markdown it was written in, or null when there is none. */
export function overviewText(web: WebViewDto): string | null {
  return typeBlock(web)?.Overview?.trim() || null
}

/** What the page calls this web in a sentence: "describe the event with it". */
export function webNoun(kind: WebHomeKind): string {
  switch (kind) {
    case 'OnlineEvent': return 'event'
    case 'Download': return 'download'
    case 'Meeting': return 'meeting'
    case 'Demo': return 'demo'
    default: return 'web'
  }
}

/** A list the page numbers — the agenda, or the reasons to download or book — with blank entries dropped. */
export function listItems(items: string[] | null | undefined): string[] {
  return (items ?? []).map(item => item.trim()).filter(Boolean)
}

/**
 * Where an online event takes place, in words.
 *
 * ⚑ An online event runs in our own room or on BigMarker (ORB-1086). A room's viewer link is set at
 * sign-off, which also gives it a ConferenceId of our own, so a conference says nothing about BigMarker
 * there. A BigMarker conference is either booked, missing from BigMarker, or not made yet. The last is
 * the state the readiness list catches as a missing channel; saying it here too is the fact, not a
 * second readiness check.
 */
export function eventWhere(event: OnlineEventViewDto): string {
  if (isOrbitalXRoom(event)) return event.ConferenceId ? 'OrbitalX room, viewer link set' : 'OrbitalX room, viewer link set at sign-off'
  if (event.ConferenceNotFound) return 'BigMarker, conference not found'
  if (event.ConferenceId || event.ConferenceJoinUrl) return 'BigMarker, conference booked'
  return 'BigMarker, no conference yet'
}

/**
 * A recording people watch whenever they like, rather than a session at a time.
 *
 * ⚑ Its availability is the web's `LaunchDate`, and the emails describe it as "Available on demand".
 * It can still carry a start: `StartDateTimeNotAllowedForOnDemandMessage` is declared and enforced
 * nowhere, a scheduled event switched to on-demand keeps the start it had, and an import sets one
 * whatever the mode. So an on-demand web ignores its start everywhere on this page, not only when blank.
 */
export function isOnDemand(event: Pick<OnlineEventViewDto, 'ModeType'>): boolean {
  return isOnDemandMode(event.ModeType)
}

/**
 * Where an online event stands against the clock, for the goal panel.
 *
 * ⚑ An on-demand recording is available from the web's launch date, and has run once that day
 * arrives — without this it never ran, and its attended and replay never appeared. A start it still
 * carries is ignored, as the header and When ignore it (`isOnDemand` says why one can be there).
 */
export function eventPhase(web: WebViewDto, now: Date = new Date()): EventClock | null {
  const event = web.OnlineEvent
  if (!event) return null
  if (isOnDemand(event)) {
    const days = calendarDaysUntil(web.LaunchDate, now)
    if (days === null) return null
    return days > 0 ? { phase: 'ahead', days } : { phase: 'run' }
  }
  return eventClock(event.StartDateTime, event.EndDateTime, event.TimeZone, now)
}

/**
 * An online event's When, Where and Format, above its overview. When is left out if there is no start.
 *
 * An on-demand recording says when it is available from instead of a time, as the emails do.
 */
export function eventFacts(event: OnlineEventViewDto, launchDate: string | null | undefined): { label: string; value: string; mono?: boolean }[] {
  const facts: { label: string; value: string; mono?: boolean }[] = []
  if (isOnDemand(event)) {
    const from = calendarDate(launchDate)
    facts.push({ label: 'When', value: from ? `Available on demand from ${from}` : 'Available on demand' })
  } else {
    const when = eventSpan(event.StartDateTime, event.EndDateTime, event.TimeZone)
    if (when) facts.push({ label: 'When', value: when, mono: true })
  }
  facts.push({ label: 'Where', value: eventWhere(event) })
  const format = eventFormat(event)
  if (format) facts.push({ label: 'Format', value: format })
  return facts
}

/** An online event's kind and mode as one phrase: "Webinar event, hybrid (live then on-demand)". */
export function eventFormat(event: OnlineEventViewDto): string | null {
  const type = event.EventType?.trim()
  const mode = event.ModeType?.trim()
  const lowered = mode ? mode.charAt(0).toLowerCase() + mode.slice(1) : null
  if (type && lowered) return `${type}, ${lowered}`
  return type || mode || null
}

/** The photo a speaker's avatar shows. The resolved value comes first; the raw override is its source. */
export function headshot(speaker: OnlineEventSpeakerViewDto): string | null {
  return speaker.ProfilePicUrl || speaker.CustomHeadshotUrl || null
}

/** A speaker's job and company, as the line under their name. */
export function speakerLine(speaker: OnlineEventSpeakerViewDto): string {
  return [speaker.Title, speaker.CompanyName].map(part => part?.trim()).filter(Boolean).join(', ')
}

/**
 * The file's kind, as the short label on its icon: `PDF`, `DOCX`.
 *
 * The file name's extension first, because that is what the reader will see when they open it; the
 * content type's subtype when the name has none. Null when neither says anything usable.
 */
export function fileKind(fileName: string | null | undefined, contentType: string | null | undefined): string | null {
  const extension = /\.([a-z0-9]{1,5})$/i.exec(fileName?.trim() ?? '')?.[1]
  if (extension) return extension.toUpperCase()
  const subtype = contentType?.split(';')[0].split('/')[1]?.trim()
  if (!subtype) return null
  if (subtype === 'pdf') return 'PDF'
  return /^[a-z0-9]{1,5}$/i.test(subtype) ? subtype.toUpperCase() : null
}

/** The line under the file's name: `PDF · 4.2 MB`, with whichever half is known. */
export function fileLine(download: NonNullable<WebViewDto['Download']>): string | null {
  const kind = fileKind(download.FileName, download.FileContentType)
  const size = typeof download.FileSizeBytes === 'number' && download.FileSizeBytes > 0 ? formatBytes(download.FileSizeBytes) : null
  return [kind, size].filter(Boolean).join(' · ') || null
}

/**
 * A booking link, as the page can safely show it.
 *
 * ⚑⚑ `href` only for an http or https URL. The value is typed by a person, and an `<a href>` built
 * from an arbitrary string runs `javascript:` on click — so anything else renders as text, not as a
 * link. `shown` is the address without its scheme or a trailing slash, as the board draws it.
 */
export function bookingLink(value: string | null | undefined): { href: string | null; shown: string; provider: string | null } | null {
  const raw = value?.trim()
  if (!raw) return null
  let url: URL | null = null
  try {
    url = new URL(raw)
  } catch {
    url = null
  }
  const safe = url !== null && (url.protocol === 'https:' || url.protocol === 'http:')
  const shown = raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
  const host = safe ? url!.hostname.toLowerCase() : ''
  const provider = host === 'calendly.com' || host.endsWith('.calendly.com') ? 'Calendly' : null
  return { href: safe ? url!.href : null, shown, provider }
}

/** When a quiz asks for the person's details, as the facts list says it. */
export function detailsPlacement(quiz: QuizDto): string {
  return quiz.DetailsPlacement === 'Before' ? 'Before the questions' : 'After the questions'
}

/** A count with its noun, agreeing with itself: `1 item`, `4 items`. */
export function counted(count: number, one: string, many: string): string {
  return `${count.toLocaleString('en-GB')} ${count === 1 ? one : many}`
}
